'use client'

import { track } from '@amplitude/analytics-browser'

import { Link } from '@/i18n/navigation'

/**
 * America's 250th announcement banner — a slim, full-width promo strip shown at
 * the top of the homepage and C2C route pages during the offer window. Ported
 * from the Claude Design canvas ("July4 Announcement Banner.dc.html"): dark-teal
 * strip, animated sparkle, and a "Learn More" CTA linking to the America's
 * 250th landing page.
 *
 * Rendering is date-gated by the caller via `isAmerica250PromoActive()`; the
 * banner is not dismissible. The whole strip is clickable (stretched-link
 * overlay on the CTA).
 */

const FINE_PRINT = 'Book in the app through Jul 3 · Offer may end early'
const CTA_LABEL = 'Learn More'
const CTA_ARIA = "America's 250th sale — 10% off US trips in the app. Learn more."

/**
 * Marketing engagement event — see the nexus catalog
 * (.docs/knowledge/analytics-events.md). `Promotion Clicked` fires on the CTA,
 * tracking the marketing-event spec proposed in strategy PR #358
 * (amplitude-events-marketing.md, pending merge).
 *
 * A custom event (not autocapture) is required: prod disables the
 * `elementInteractions` / `formInteractions` autocapture categories, so a raw
 * click on the CTA would be filtered server-side and never land.
 *
 * `page_type` is passed by the caller because the spec requires it on every event
 * and the nexus enrichment plugin only emits `landing_c2c` (never `homepage`), so
 * the homepage surface can't be derived from enrichment. The global `source` /
 * `device_form_factor` plugins still enrich automatically.
 */
const PROMOTION_CLICKED_EVENT = 'Promotion Clicked'
const PROMOTION_NAME = 'america_250'
const CTA_HREF = '/america-250'

type PromoPageType = 'homepage' | 'landing_c2c'

function trackPromotionClicked(pageType: PromoPageType): void {
    track(PROMOTION_CLICKED_EVENT, {
        promotion_name: PROMOTION_NAME,
        page_type: pageType,
        link_url: CTA_HREF,
    })
}

function Sparkle({ className }: { className?: string }) {
    return (
        <svg
            viewBox="0 0 40 40"
            fill="none"
            aria-hidden="true"
            className={`block flex-none ${className ?? ''}`}
        >
            {/* big central 4-point sparkle */}
            <path
                d="M20 5 C21 15 25 19 35 20 C25 21 21 25 20 35 C19 25 15 21 5 20 C15 19 19 15 20 5 Z"
                fill="#66D9E6"
            />
            {/* secondary small sparkle (twinkles) */}
            <path
                d="M32 6 C32.5 10 34 11.5 38 12 C34 12.5 32.5 14 32 18 C31.5 14 30 12.5 26 12 C30 11.5 31.5 10 32 6 Z"
                fill="#D13B3B"
                className="origin-center animate-[america250-banner-twinkle_2.4s_ease-in-out_infinite]"
            />
            {/* tiny dot (twinkles, offset) */}
            <circle
                cx="8"
                cy="30"
                r="2"
                fill="#99E5EE"
                className="origin-center animate-[america250-banner-twinkle_1.8s_ease-in-out_0.6s_infinite]"
            />
        </svg>
    )
}

export function July4Banner({ pageType }: { pageType: PromoPageType }) {
    return (
        <aside
            aria-label="America's 250th promotion"
            className="relative isolate overflow-hidden bg-[#003940]"
        >
            {/* single row on all breakpoints: sparkle | copy | (desktop fine print) | CTA */}
            <div className="relative mx-auto flex max-w-[1056px] items-center gap-3 px-4 py-2.5 md:min-h-[64px] md:gap-5 md:px-10 md:py-0">
                <Sparkle className="h-[22px] w-[22px] md:h-[30px] md:w-[30px]" />

                {/* copy block: eyebrow above headline; fine print below on mobile */}
                <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[10px] font-bold tracking-[0.18em] text-[#99E5EE] uppercase md:text-[11px]">
                        America&apos;s 250th
                    </span>
                    <span className="text-[14px] leading-[1.2] font-bold text-[#F7F8FA] md:text-[19px] md:leading-[1.15] md:whitespace-nowrap">
                        <span className="text-[#CCF2F7]">10%</span> off every US trip in the app
                    </span>
                    <span className="text-[11px] font-medium text-[#B3ECF2] md:hidden">
                        {FINE_PRINT}
                    </span>
                </div>

                {/* fine print (desktop, inline after the copy block) */}
                <span className="ml-1 hidden text-[12.5px] font-medium whitespace-nowrap text-[#B3ECF2] md:inline">
                    {FINE_PRINT}
                </span>

                {/* CTA — real link; its stretched overlay makes the whole strip clickable */}
                <Link
                    href={CTA_HREF}
                    aria-label={CTA_ARIA}
                    onClick={() => trackPromotionClicked(pageType)}
                    className="ml-auto flex h-9 flex-none items-center justify-center rounded-[4px] bg-[#D13B3B] px-3.5 text-[13px] font-bold text-[#F7F8FA] no-underline transition-colors after:absolute after:inset-0 after:content-[''] hover:bg-[#B72424] md:h-11 md:px-[22px] md:text-[14.5px]"
                >
                    {CTA_LABEL}
                </Link>
            </div>
        </aside>
    )
}
